import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useBlocker, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, ExternalLink as ExtIcon } from "lucide-react";

// ---- Back navigation: real history when we have it, sensible fallback otherwise ----
export function hasInAppHistory() {
  return (window.history.state?.idx ?? 0) > 0;
}

export function useBack(fallback = "/") {
  const navigate = useNavigate();
  return useCallback(() => { if (hasInAppHistory()) navigate(-1); else navigate(fallback, { replace: true }); }, [navigate, fallback]);
}

export function BackButton({ fallback = "/", label = "Back", className = "", testid = "back-button", onClick }) {
  const back = useBack(fallback);
  return (
    <button type="button" data-testid={testid} onClick={onClick || back} className={`inline-flex items-center gap-1.5 mono-label text-[#A1A1AA] hover:text-[#E8552A] transition-colors ${className}`}>
      <ArrowLeft size={15} /> {label}
    </button>
  );
}

// ---- Breadcrumbs: every item except the last is a real link ----
export function Breadcrumbs({ items = [], className = "" }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className={`flex flex-wrap items-center gap-1 mono-label text-[#71717A] ${className}`}>
      {items.map((it, i) => {
        const last = i === items.length - 1;
        return (
          <span key={`${it.label}-${i}`} className="flex items-center gap-1 min-w-0">
            {i > 0 && <ChevronRight size={12} className="shrink-0 text-[#3F3F46]" />}
            {last || !it.to ? <span data-testid={`crumb-${i}`} className={`truncate ${last ? "text-[#F4F4F5]" : ""}`}>{it.label}</span>
              : <Link data-testid={`crumb-${i}`} to={it.to} className="truncate hover:text-[#E8552A]">{it.label}</Link>}
          </span>
        );
      })}
    </nav>
  );
}

export function PageHeader({ title, crumbs, fallback = "/", backLabel = "Back", right, children, className = "" }) {
  return (
    <div className={`mb-6 ${className}`} data-testid="page-header">
      <div className="flex items-center gap-4 flex-wrap"><BackButton fallback={fallback} label={backLabel} />{crumbs && <Breadcrumbs items={crumbs} />}</div>
      {(title || right) && <div className="flex items-center justify-between gap-3 mt-2">{title && <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">{title}</h1>}{right}</div>}
      {children}
    </div>
  );
}

// ---- External links are visibly external and never touch app history ----
export function ExternalLink({ href, children, className = "", ...p }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 ${className}`} {...p}>{children} <ExtIcon size={12} className="shrink-0" /></a>;
}

// ---- Escape closes dismissible dialogs ----
export function useEscape(onClose, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, enabled]);
}

// ---- Unsaved-changes protection (route changes, tab changes, browser close) ----
const DirtyCtx = createContext({ register: () => () => {} });

export function UnsavedChangesProvider({ children }) {
  const forms = useRef(new Map());
  const [, force] = useState(0);
  const anyDirty = () => [...forms.current.values()].some((f) => f.dirty);
  const register = useCallback((id, entry) => { forms.current.set(id, entry); force((x) => x + 1); return () => { forms.current.delete(id); force((x) => x + 1); }; }, []);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => anyDirty() && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  useEffect(() => {
    const h = (e) => { if (anyDirty()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);
  const dirtyEntry = [...forms.current.values()].find((f) => f.dirty);
  return (
    <DirtyCtx.Provider value={{ register }}>
      {children}
      {blocker.state === "blocked" && (
        <UnsavedDialog
          onStay={() => blocker.reset()}
          onLeave={() => { forms.current.forEach((f) => (f.dirty = false)); blocker.proceed(); }}
          onSave={dirtyEntry?.save ? async () => { const ok = await dirtyEntry.save(); if (ok !== false) { forms.current.forEach((f) => (f.dirty = false)); blocker.proceed(); } } : null}
        />
      )}
    </DirtyCtx.Provider>
  );
}

export function UnsavedDialog({ onStay, onLeave, onSave }) {
  useEscape(onStay);
  return (
    <div className="fixed inset-0 z-[100] bg-black/75 flex items-center justify-center p-4" data-testid="unsaved-dialog" onClick={(e) => e.stopPropagation()}>
      <div className="bg-[#121216] border border-[#27272A] rounded-2xl w-full max-w-sm p-6 fade-up">
        <p className="mono-label text-[#FF9F1C]">UNSAVED CHANGES</p>
        <h3 className="font-display text-xl font-bold mt-1">You have unsaved changes.</h3>
        <p className="text-sm text-[#A1A1AA] mt-1">Leave without saving?</p>
        <div className="flex flex-col gap-2 mt-5">
          <button data-testid="unsaved-stay" onClick={onStay} className="copper-btn rounded-lg py-2.5 mono-label">CONTINUE EDITING</button>
          {onSave && <button data-testid="unsaved-save" onClick={onSave} className="border border-[#10B981]/50 text-[#10B981] rounded-lg py-2.5 mono-label">SAVE CHANGES</button>}
          <button data-testid="unsaved-leave" onClick={onLeave} className="border border-[#ef4444]/50 text-[#ef4444] rounded-lg py-2.5 mono-label">LEAVE WITHOUT SAVING</button>
        </div>
      </div>
    </div>
  );
}

/** Track a form's dirty state. Returns { dirty, guard(fn) } — guard wraps a close/cancel handler with the unsaved dialog. */
export function useUnsaved(dirty, save) {
  const { register } = useContext(DirtyCtx);
  const id = useRef(`f_${Math.random().toString(36).slice(2)}`);
  const [pending, setPending] = useState(null);
  const entry = useRef({ dirty, save });
  entry.current.dirty = dirty; entry.current.save = save;
  useEffect(() => register(id.current, entry.current), [register]);
  const guard = useCallback((fn) => () => { if (entry.current.dirty) setPending(() => fn); else fn(); }, []);
  const dialog = pending ? <UnsavedDialog onStay={() => setPending(null)} onLeave={() => { entry.current.dirty = false; const fn = pending; setPending(null); fn(); }} onSave={save ? async () => { const ok = await save(); if (ok !== false) { entry.current.dirty = false; const fn = pending; setPending(null); fn(); } } : null} /> : null;
  return { dirty, guard, dialog };
}

/** Keep a piece of UI state in the URL search params (push = new history entry, replace = same entry). */
export function useUrlState(key, def = "", { replace = false } = {}) {
  const loc = useLocation();
  const navigate = useNavigate();
  const sp = new URLSearchParams(loc.search);
  const value = sp.get(key) ?? def;
  const set = useCallback((v) => {
    const n = new URLSearchParams(window.location.search);
    if (v === "" || v == null || v === def) n.delete(key); else n.set(key, v);
    navigate({ pathname: window.location.pathname, search: n.toString() ? `?${n}` : "" }, { replace });
  }, [navigate, key, def, replace]);
  return [value, set];
}

export function fromLocation(loc) {
  return `${loc.pathname}${loc.search || ""}${loc.hash || ""}`;
}
